import { type FC, useEffect } from 'react'
import classNames from 'classnames'
import * as Sentry from '@sentry/react'
import { Button } from '../../../../../components/Button/Button'
import { type AccountSchedulingSettings, MeetingMode, type SchedulingEventType } from '../../../../../models/Scheduling'
import { useAppSelector } from '../../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { BOOKING_BASE_URL } from '../../../../Scheduling/shared/bookingBaseUrl'
import { ownRedtailEntry, REDTAIL_NOT_CONFIGURED_LABEL } from '../../../../Scheduling/shared/eventConfig'
import { useRedtailEntityOptions, useRedtailEventTypeAccess } from '../../../../Scheduling/shared/useRedtailEventConfig'

interface EventTypeSummaryCardProps {
  draft: SchedulingEventType
  accountSettings?: AccountSchedulingSettings
  disabled: boolean
  onEdit: () => void
}

const REDTAIL_UNKNOWN_LABEL = '—'

export const EventTypeSummaryCard: FC<EventTypeSummaryCardProps> = ({ draft, accountSettings, disabled, onEdit }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId

  const redtailAccess = useRedtailEventTypeAccess(accountId, draft.access)
  const { activityCodeOptions, categoryOptions } = useRedtailEntityOptions(accountId, redtailAccess.enabled)
  const ownEntry = redtailAccess.enabled ? ownRedtailEntry(draft.eventConfig, redtailAccess.redtailAccountId) : undefined
  const activityCodeLabel = ownEntry ? activityCodeOptions.find((option) => option.id === ownEntry.activityCodeId.toString())?.label : undefined
  const categoryLabel = ownEntry ? categoryOptions.find((option) => option.id === ownEntry.categoryId.toString())?.label : undefined

  const accountSlug = accountSettings?.publicBookingPage.accountSlug

  useEffect(() => {
    if (accountSettings && !accountSlug) {
      Sentry.captureException(new Error('Scheduling: account is missing accountSlug; cannot build a booking URL in the event type summary card'))
    }
  }, [accountSettings, accountSlug])

  return (
    <div className='d-flex flex-column gap-2 p-3 border rounded-3 zocks-border-color-gray-300 zocks-bg-color-white'>
      <div className='d-flex align-items-start justify-content-between gap-2'>
        <span className='bold paragraph-large'>{draft.name}</span>
        {!disabled ? (
          <Button variant='grayButton' className='p-2' aria-label='Edit event type' onClick={onEdit}>
            <i className='bi bi-pencil' />
          </Button>
        ) : null}
      </div>
      <div className='d-flex align-items-center zocks-gap-0.5 paragraph-small zocks-text-gray-900'>
        <span>{draft.durationMinutes} minutes</span>
        <span>·</span>
        <span className='d-inline-flex align-items-center zocks-gap-0.25'>
          <i className={draft.meetingMode === MeetingMode.VIRTUAL ? 'bi bi-camera-video' : 'bi bi-people'} />
          {draft.meetingMode === MeetingMode.VIRTUAL ? 'Virtual' : 'In person'}
        </span>
      </div>
      {draft.description ? <div className='paragraph-small zocks-text-gray-700'>{draft.description}</div> : null}
      {redtailAccess.enabled ? (
        <div className='d-flex flex-column zocks-gap-0.25 pt-2'>
          <span className='bold paragraph-small zocks-text-gray-700'>Redtail Activity match</span>
          {ownEntry ? (
            <div className='d-flex gap-1'>
              <div className='d-flex flex-column gap-1'>
                <span className='zocks-text-gray-600 paragraph-small'>Type:</span>
                <span className='zocks-text-gray-600 paragraph-small'>Category:</span>
              </div>
              <div className='d-flex flex-column gap-1'>
                <span className='paragraph-small'>{activityCodeLabel ?? REDTAIL_UNKNOWN_LABEL}</span>
                <span className='paragraph-small'>{categoryLabel ?? REDTAIL_UNKNOWN_LABEL}</span>
              </div>
            </div>
          ) : (
            <span className='paragraph-small zocks-text-gray-600'>{REDTAIL_NOT_CONFIGURED_LABEL}</span>
          )}
        </div>
      ) : null}
      <div className={classNames('d-flex align-items-center zocks-gap-0.5 paragraph-small', accountSlug ? 'zocks-text-gray-600' : 'zocks-text-danger')}>
        <i className='bi bi-link-45deg flex-shrink-0' />
        <span className='text-truncate'>{accountSlug ? `${BOOKING_BASE_URL}/${accountSlug}/.../${draft.slug}` : 'Booking URL unavailable'}</span>
      </div>
    </div>
  )
}
